import { Box, Grid, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Section } from '../../../../../components/ui/Section';
import {
  EsgActionItem,
  RISK_LEVELS,
  RISK_LEVEL_COLOR,
  SupplierEsgProfile,
  buildRiskHeatmap,
  getRecommendedActions,
} from '../../../../../mocks/data/supplierEsgDashboardData';
import EsgActionItemsTable from './EsgActionItemsTable';

export interface RiskActionsTabProps {
  profiles: SupplierEsgProfile[];
  actionItems: EsgActionItem[];
}

const RiskActionsTab: React.FC<RiskActionsTabProps> = ({ profiles, actionItems }) => {
  const { t } = useTranslation('esgDashboard');
  const heatmap = buildRiskHeatmap(profiles);
  const recommendations = getRecommendedActions(profiles);

  const byCategory = (category: EsgActionItem['category']) => actionItems.filter((item) => item.category === category);

  return (
    <Grid container
      spacing={4}>
      <Grid item
        xs={12}>
        <Section style={{ boxShadow: 'none' }}>
          <Box sx={{ p: 4 }}>
            <Typography variant="textSM"
              fontWeight={600}
              sx={{ mb: 2 }}>{t('risk.heatmapTitle', 'Risk heatmap - category x risk level (supplier count)')}</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('common.category', 'Category')}</TableCell>
                  {RISK_LEVELS.map((risk) => (
                    <TableCell key={risk}
                      align="center">{risk}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {heatmap.map((row) => (
                  <TableRow key={row.category}>
                    <TableCell>{row.category}</TableCell>
                    {RISK_LEVELS.map((risk) => (
                      <TableCell key={risk}
                        align="center">
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 32,
                            height: 32,
                            borderRadius: 1,
                            color: row.counts[risk] > 0 ? '#fff' : 'text.secondary',
                            backgroundColor: row.counts[risk] > 0 ? RISK_LEVEL_COLOR[risk] : 'transparent',
                            fontWeight: 600,
                            fontSize: '12px',
                          }}
                        >
                          {row.counts[risk]}
                        </Box>
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Section>
      </Grid>

      <Grid item
        xs={12}
        md={6}>
        <Section style={{ boxShadow: 'none' }}>
          <Box sx={{ p: 4 }}>
            <EsgActionItemsTable title={t('risk.expiringCertifications', 'Expiring certifications')}
              items={byCategory('Certification')} />
          </Box>
        </Section>
      </Grid>
      <Grid item
        xs={12}
        md={6}>
        <Section style={{ boxShadow: 'none' }}>
          <Box sx={{ p: 4 }}>
            <EsgActionItemsTable title={t('risk.expiringInsurance', 'Expiring insurance')}
              items={byCategory('Insurance')} />
          </Box>
        </Section>
      </Grid>
      <Grid item
        xs={12}
        md={6}>
        <Section style={{ boxShadow: 'none' }}>
          <Box sx={{ p: 4 }}>
            <EsgActionItemsTable title={t('risk.missingDocuments', 'Missing documents')}
              items={byCategory('Documentation')} />
          </Box>
        </Section>
      </Grid>
      <Grid item
        xs={12}
        md={6}>
        <Section style={{ boxShadow: 'none' }}>
          <Box sx={{ p: 4 }}>
            <EsgActionItemsTable title={t('risk.overdueAudits', 'Overdue audits')}
              items={byCategory('Audit')} />
          </Box>
        </Section>
      </Grid>
      <Grid item
        xs={12}
        md={6}>
        <Section style={{ boxShadow: 'none' }}>
          <Box sx={{ p: 4 }}>
            <EsgActionItemsTable title={t('risk.outstandingCorrectiveActions', 'Outstanding corrective actions')}
              items={byCategory('CorrectiveAction')} />
          </Box>
        </Section>
      </Grid>
      <Grid item
        xs={12}
        md={6}>
        <Section style={{ boxShadow: 'none' }}>
          <Box sx={{ p: 4 }}>
            <EsgActionItemsTable title={t('risk.missingQuestionnaires', 'Missing ESG questionnaires')}
              items={byCategory('Questionnaire')} />
          </Box>
        </Section>
      </Grid>
      <Grid item
        xs={12}>
        <Section style={{ boxShadow: 'none' }}>
          <Box sx={{ p: 4 }}>
            <EsgActionItemsTable title={t('risk.complianceBreaches', 'Compliance breaches')}
              items={byCategory('ComplianceBreach')} />
          </Box>
        </Section>
      </Grid>

      <Grid item
        xs={12}>
        <Section style={{ boxShadow: 'none' }}>
          <Box sx={{ p: 4 }}>
            <Typography variant="textSM"
              fontWeight={600}
              sx={{ mb: 2 }}>{t('risk.recommendedActionsTitle', 'Recommended next actions')}</Typography>
            {recommendations.length === 0 ? (
              <Typography variant="textXS"
                color="text.secondary">{t('risk.noRecommendations', 'No high or critical risk suppliers in the current filter.')}</Typography>
            ) : (
              <Stack spacing={1}>
                {recommendations.map((rec) => (
                  <Typography key={rec.supplierId}
                    variant="textSM">
                    <b>{rec.supplierName}:</b> {rec.recommendation}
                  </Typography>
                ))}
              </Stack>
            )}
          </Box>
        </Section>
      </Grid>
    </Grid>
  );
};

export default RiskActionsTab;
